import React, { memo, useEffect, useMemo, useRef } from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { UltrarubigramStore } from '../UltrarubigramStore';
import { flipGrid, rotatePuzzle, solvedGrids } from '../ultrarubigramUtils';
import { inverse, rotationElement } from '../geometry';
import Grid from './Grid';
import RotateButton from './RotateButton';

const Board = memo(() => {
  // Selected individually so the timer ticking every second doesn't re-render the board.
  const { rot, grids, solution, solved, revealed, isRevealing, isCelebrating } = UltrarubigramStore.useState(s => ({
    rot: s.rot, grids: s.grids, solution: s.solution, solved: s.solved,
    revealed: s.revealed, isRevealing: s.isRevealing, isCelebrating: s.isCelebrating,
  }));

  // Once the puzzle is finished, later visits show the solution as a plain grid. The visit in
  // which it was finished keeps the live pieces, so the final move and the victory animation
  // play out on them. "On load" allows for a saved state that arrives just after mounting.
  const wasSolvedOnLoad = useSolvedOnLoad(solved);
  const revealedOnLoad = useRef(revealed && !isRevealing).current;
  const showStatic = wasSolvedOnLoad || revealedOnLoad;

  const staticGrids = useMemo(() => (showStatic ? solvedGrids(solution) : null), [showStatic, solution]);
  const shownGrids = staticGrids ?? grids;
  const shownRot = staticGrids ? 0 : rot;
  const interactive = !showStatic;

  const root = rotationElement(shownRot);

  // While a 6x6 or 12x12 is moving it passes over every tab outside it; a moving 3x3 passes
  // under its 6x6's tabs instead (see .bigMoving).
  const bigMoving = shownGrids.some(grid => grid.moving || grid.quadrants.some(quadrant => quadrant.moving));

  const rotationStyle: React.CSSProperties = {
    transform: `rotate(${shownRot}deg)`,
  };

  const frameClasses = 'ultraBoardFrame'
    + (bigMoving ? ' bigMoving' : '')
    + (isCelebrating && !showStatic ? ' celebrating' : '');

  return (
    <div className={frameClasses} onContextMenu={(e) => { e.preventDefault(); }}>
      <div className="ultraRotation" style={rotationStyle}>
        {/*
          Every tile's border bands, in a copy of the piece tree under the real one. There they
          can never cover a flip tab (which lies on a 3x3's edge, over its neighbours' bands);
          a moving piece shows its own bands on top for as long as it moves (see .isMoving).
        */}
        <div className="ultraBandLayer">
          {shownGrids.map((grid, g) => (
            <Grid
              key={g}
              g={g}
              grid={grid}
              parent={root}
              interactive={false}
              layer="bands"
            />
          ))}
        </div>

        {shownGrids.map((grid, g) => (
          <Grid
            key={g}
            g={g}
            grid={grid}
            parent={root}
            interactive={interactive}
          />
        ))}

        {interactive && <>
          {/* The 6x6 flip tabs on the 12x12 seams, in copies of the grids that sit above all of them. */}
          {shownGrids.map((grid, g) => (
            <Grid
              key={`seamTabs${g}`}
              g={g}
              grid={grid}
              parent={root}
              interactive
              layer="seamTabs"
            />
          ))}

          <RotateButton level="puzzle" upright={inverse(root)} onRotate={rotatePuzzle}/>

          {[0, 1, 2, 3].map(g => (
            <React.Fragment key={g}>
              <button
                type="button"
                className={`ultraOuterFlip vertical g${g}`}
                onClick={() => flipGrid(g, 'V')}
              >
                <SwapVertIcon/>
              </button>
              <button
                type="button"
                className={`ultraOuterFlip horizontal g${g}`}
                onClick={() => flipGrid(g, 'H')}
              >
                <SwapHorizIcon/>
              </button>
            </React.Fragment>
          ))}
        </>}
      </div>
    </div>
  );
});

/** Whether the puzzle was already solved when the board appeared (or within its first moments). */
const useSolvedOnLoad = (solved: boolean) => {
  const mountedAt = useRef(Date.now());
  const solvedOnLoad = useRef(solved);
  useEffect(() => {
    if (solved && Date.now() - mountedAt.current < 300) solvedOnLoad.current = true;
  }, [solved]);
  return solvedOnLoad.current;
};

export default Board;
